import { Fragment, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom'
import { Add, DeleteOutline } from '@material-ui/icons'

import { OutletWrapper, TOAST_TYPE } from '@base'
import { useHeaderMeta } from '@hooks'
import { Button, Calendar, Input, Select, Table } from '@baseV2'
import { DRAWER_TYPES, LOGISTICS_HANDLER_TYPES, enquiryInputType, strings } from '@constants'
import { getLogisticsByEnquiryId } from '@data'
import convertPagableData from '@data/conn/convertPagableData'
import { getInputType } from '@base/composite/Table/TableUtils/Columns'
import { getApprovedFulfillmentPlansApi, getDispatchPlanDraftApi } from '@data/conn/get/shipmentRequest'
import { createShipmentDispatchPlansApi } from '@data/conn/post/shipmentRequest'
import { User } from '@data/model'
import { showLoader, showToast } from '@data/state/action'
import { toggleDrawer } from '@data/state/action/root'
import { createDispatchPlanBreadCrumbs, decimalSeparatedNumber, displayAmount, displayName, formatDate } from '@utils'
import { Sizes } from '@styles'
import { paths } from '@routes'
import ShipmentTabs from '@pages/Admin/Orders/ManageOrder/Shipment/CreateShipment/ShipmentTabs'
import {
	CreateShipmentPage,
	DetailField,
	DetailLabel,
	DetailValue,
	GridListWrapper,
	LeftSection,
	SectionBlock,
	SectionHeading,
	StickyFooter,
} from '@pages/Admin/Orders/ManageOrder/Shipment/styles'

import {
	AddDispatchPlanHeader,
	AddDispatchPlanLogistics,
	DispatchFieldsRow,
	DispatchPlanGrid,
	DispatchSectionBlock,
	RightCards,
	VehicleCardHeader,
	VehicleRemoveButton,
} from '../style'

// organisations are shown by their company name, people by their own name
const orgName = (user) => user?.organisation_name || user?.full_name || '-'

// each option is an approved fulfillment plan, in the same order as the select's label: Buyer | Buyer PO | Supplier | Supplier PO | Fulfillment Id
const displayPlanName = (plan) =>
	[
		orgName(plan?.buyer),
		plan?.original_buyer_po_number || '-',
		orgName(plan?.supplier),
		plan?.original_supplier_po_number || '-',
		plan?.fulfillment_plan_id || '-',
	].join(' | ')
const personName = (user) => user?.full_name || '-'

// "City, State" of an address, whichever of the two it has
const cityState = (address) => [address?.city, address?.state].filter(Boolean).join(', ') || '-'

// weights come in kgs for WEIGHT items, in their own unit otherwise
export const displayItemWeight = (weight, inputType) => {
	if (weight == null) return '-'
	const value = inputType === enquiryInputType.WEIGHT ? Number(weight) / 1000 : Number(weight)
	return `${decimalSeparatedNumber(value)} ${getInputType(inputType)}`
}

// "Quantity (MT)" — the unit comes from the draft's input_type
export const quantityLabel = (inputType) => (inputType ? `${strings('quantity')} (${getInputType(inputType)})` : strings('quantity'))

// label/value pairs of the left section, read off the dispatch plan draft. `span: 2` fills the whole row
export const getDraftDetails = (draft) => [
	{ label: strings('buyer_name'), value: orgName(draft?.buyer), span: 2 },
	{ label: strings('supplier_name'), value: orgName(draft?.supplier), span: 2 },
	{ label: strings('dispatch_location'), value: cityState(draft?.dispatch_location) },
	{ label: strings('shipping_location'), value: cityState(draft?.shipping_address) },
	{ label: strings('buyer_po'), value: draft?.original_buyer_po_number || '-' },
	{ label: strings('supplier_po'), value: draft?.original_supplier_po_number || '-' },
	{ label: strings('sales_person'), value: personName(draft?.sales_person) },
	{ label: strings('supply_person'), value: personName(draft?.supply_person) },
	{ label: strings('expected_amount'), value: draft?.expected_amount != null ? displayAmount(draft.expected_amount) : '-' },
	{ label: strings('fulfillment_quantity'), value: displayItemWeight(draft?.fulfillment_weight, draft?.input_type) },
	{ label: strings('fulfillment_start_date'), value: formatDate(draft?.fulfillment_start_date) },
	{ label: strings('fulfillment_end_date'), value: formatDate(draft?.fulfillment_end_date) },
	{ label: strings('dispatch_quantity'), value: displayItemWeight(draft?.total_dispatch_weight, draft?.input_type) },
	{ label: strings('expected_transit_time'), value: draft?.expected_transit_time ?? '-' },
]

const logisticsHandlerTypes = Object.entries(LOGISTICS_HANDLER_TYPES).map(([key, value]) => ({ key, label: value }))

// 10 digit indian mobile number
export const DRIVER_NUMBER_PATTERN = /^[6-9][0-9]{9}$/

// only the quantity of a vehicle is mandatory, its number and driver can be filled in later
const emptyVehicle = (id) => ({ id, vehicle_number: '', driver_number: '', quantity: '' })

// id ties the form to its tab. The logistics handler and partner are common to every tab: a new tab
// takes them from the existing ones, the first tab's handler from the fulfillment plan.
const emptyDispatchForm = (id, logisticsHandler = null, logisticsPartner = null) => ({
	id,
	plan_start_date: { label: strings('plan_start_date'), value: null, placeholder: strings('select', 'date'), disabled: false, required: true },
	// the sum of the vehicle quantities, never typed in
	quantity: { label: strings('quantity'), value: '', placeholder: strings('quantity'), disabled: true, required: true },
	logistics_handler: {
		label: strings('logistics_handler'),
		value: logisticsHandler,
		placeholder: strings('select', 'logistics_handler'),
		// comes from the fulfillment plan and isn't changed here
		disabled: true,
		required: true,
	},
	logistics_partner: {
		label: strings('logistics_partner'),
		value: logisticsPartner,
		placeholder: strings('select', 'logistics_partner'),
		disabled: false,
		required: false,
	},
	// no vehicle rows until the user adds one
	vehicles: [],
})

// a dispatch's quantity is the total of its vehicles'. Rounded so float sums like 0.1 + 0.2 don't
// show their noise, and left empty while no vehicle has a quantity.
const sumVehicleQuantities = (vehicles) => {
	const total = vehicles.reduce((sum, vehicle) => sum + (Number(vehicle.quantity) || 0), 0)
	return total > 0 ? String(Math.round(total * 1000) / 1000) : ''
}

// quantities are typed in the item's own unit — MT for weight items — while the api carries weights in kgs
export const toApiWeight = (quantity, inputType) => {
	const value = Number(quantity) || 0
	return inputType === enquiryInputType.WEIGHT ? value * 1000 : value
}

// The /shipmentRequest/dispatchPlan body — one entry per dispatch tab. Every entry carries the same
// draft fields the body takes, with that tab's own date, quantity, logistics and vehicles on top.
const toDispatchPlanPayload = (dispatches, draft) =>
	dispatches.map((_dispatch) => {
		const dispatchWeight = toApiWeight(_dispatch?.quantity?.value, draft?.input_type)

		return {
			fulfillment_plan_id: draft?.fulfillment_plan_id,
			shipment_request_id: draft?.shipment_request_id,
			order_id: draft?.order_id,
			platform: draft?.platform,
			region: draft?.region,
			buyer: draft?.buyer,
			supplier: draft?.supplier,
			sales_person: draft?.sales_person,
			supply_person: draft?.supply_person,
			dispatch_location: draft?.dispatch_location,
			price_per_kg: draft?.price_per_kg,
			input_type: draft?.input_type,
			plan_start_date: _dispatch?.plan_start_date?.value,
			plan_current_date: _dispatch?.plan_start_date?.value,
			dispatch_weight: dispatchWeight,
			expected_amount: dispatchWeight * (Number(draft?.price_per_kg) || 0),
			logistics_handler: _dispatch?.logistics_handler?.value,
			logistics_partner: _dispatch?.logistics_partner?.value ? new User(_dispatch.logistics_partner.value).getUserDetails() : undefined,
			freight_lumpsum: _dispatch?.logistics_partner?.value?.lumpsum_logistics_quote,
			freight_per_mt: _dispatch?.logistics_partner?.value?.logistics_quote_per_mt,
			vehicle_details: (_dispatch?.vehicles ?? []).map((vehicle) => ({
				vehicle_number: vehicle.vehicle_number,
				driver_number: vehicle.driver_number,
				quantity: toApiWeight(vehicle.quantity, draft?.input_type),
			})),
		}
	})

// same page shell as Create Shipment: a 3/9 split on laptop+, stacked below it

const AddDispatchPlanV2 = () => {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	// set when opened from an order's Dispatch Plans tab: the plans on offer are that order's, and we go back to the tab
	const { orderId } = useParams()
	useHeaderMeta(createDispatchPlanBreadCrumbs(orderId), true, {}, [orderId], !orderId)
	// ?fulfillmentPlanId= preselects that plan once the list is in (e.g. from Orders → Pending Dispatch)
	const [searchParams] = useSearchParams()
	const preselectedPlanId = searchParams.get('fulfillmentPlanId')
	const preselectDone = useRef(false)
	const [planList, setPlanList] = useState([])
	const [selectedPlan, setSelectedPlan] = useState(null)
	// draft of the dispatch plan for the selected fulfillment plan, like the shipment draft on Create Shipment
	const [draft, setDraft] = useState(null)
	const [logisticsPartnerList, setLogisticsPartnerList] = useState([])

	// one form per dispatch tab, same as the shipment tabs on Create Shipment
	const nextDispatchId = useRef(2)
	// vehicle ids are unique across tabs, so a removed row never hands its key to another
	const nextVehicleId = useRef(1)
	const newVehicleId = () => nextVehicleId.current++
	const [dispatches, setDispatches] = useState([emptyDispatchForm(1)])
	const [activeTabId, setActiveTabId] = useState(1)
	// below laptop the form sits low on the page, so the date picker opens upwards
	const [isStacked, setIsStacked] = useState(() => window.innerWidth < Sizes.laptop)

	useEffect(() => {
		const resizeHandler = () => setIsStacked(window.innerWidth < Sizes.laptop)
		window.addEventListener('resize', resizeHandler)
		return () => window.removeEventListener('resize', resizeHandler)
	}, [])

	const activeDispatch = dispatches.find((_dispatch) => _dispatch.id === activeTabId)
	// logistics handler and partner are the same on every tab, so the header selects read them off the first
	const commonDispatch = dispatches[0]
	const tabs = dispatches.map(({ id }, index) => ({ id, label: `${strings('dispatch')} ${index + 1}` }))

	// a dispatch is complete once it has a date and a quantity
	const isDispatchComplete = (_dispatch) => !!_dispatch?.plan_start_date?.value && Number(_dispatch?.quantity?.value) > 0

	// a new tab is allowed once the open one is complete, and create once every tab is
	const canAddDispatch = isDispatchComplete(activeDispatch)
	// create also needs the logistics handler of every tab. The partner is optional.
	const isDispatchReadyToCreate = (_dispatch) => isDispatchComplete(_dispatch) && !!_dispatch?.logistics_handler?.value
	const canCreate = !!selectedPlan && !!draft && dispatches.every(isDispatchReadyToCreate)

	// Under Manage Order the Shipment tab owns a full-width action bar; this page hands its button to
	// it so the bar matches the other tab pages. Standalone it keeps its own footer (see below).
	// The handler is read through a ref so the bar is not rebuilt on every render.
	const { setFooterActions } = useOutletContext() ?? {}
	const createHandlerRef = useRef()

	useEffect(() => {
		if (typeof setFooterActions !== 'function') return
		setFooterActions(
			<Button type='button' color='primary' disabled={!canCreate} onClick={() => createHandlerRef.current?.()}>
				{strings('create')}
			</Button>
		)
		return () => setFooterActions(null)
	}, [setFooterActions, canCreate])

	// every dispatch tab goes out in a single call
	const createDispatchPlansHandler = () => {
		if (!canCreate) return

		dispatch(showLoader(true))
		createShipmentDispatchPlansApi(toDispatchPlanPayload(dispatches, draft))
			.then(() => {
				dispatch(showToast(true, strings('msg_dispatch_plan_created_success'), { type: TOAST_TYPE.SUCCESS }))
				navigate(-1)
			})
			.catch((error) => {
				const message = error?.response?.data?.message ?? strings('msg_dispatch_plan_created_failure')
				dispatch(showToast(true, message, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}

	// the dispatches already created for the selected fulfillment plan
	const previousDispatchesHandler = () => {
		dispatch(
			toggleDrawer(true, DRAWER_TYPES.PREVIOUS_DISPATCHES, {
				label: strings(['previous', 'dispatches']),
				orderId: selectedPlan?.order_id,
				fulfillmentId: selectedPlan?.fulfillment_plan_id,
			})
		)
	}

	const addDispatchHandler = () => {
		const id = nextDispatchId.current
		nextDispatchId.current += 1
		setDispatches((_dispatches) => [
			..._dispatches,
			emptyDispatchForm(id, commonDispatch?.logistics_handler?.value, commonDispatch?.logistics_partner?.value),
		])
		setActiveTabId(id)
	}

	// if the open tab is removed, the one on its left takes over
	const removeDispatchHandler = (id) => {
		const removedIndex = dispatches.findIndex((_dispatch) => _dispatch.id === id)
		const next = dispatches.filter((_dispatch) => _dispatch.id !== id)
		setDispatches(next)
		if (activeTabId === id) setActiveTabId(next[Math.max(removedIndex - 1, 0)]?.id)
	}

	// sets the value of one field of the open tab's form
	const updateActiveDispatch = (key) => (value) => {
		setDispatches((_dispatches) =>
			_dispatches.map((_dispatch) => (_dispatch.id === activeTabId ? { ..._dispatch, [key]: { ..._dispatch[key], value } } : _dispatch))
		)
	}

	// sets the value of one field on every tab's form — for the fields common to all of them
	const updateAllDispatches = (key) => (value) => {
		setDispatches((_dispatches) => _dispatches.map((_dispatch) => ({ ..._dispatch, [key]: { ..._dispatch[key], value } })))
	}

	// vehicle rows of the open tab, with the tab's quantity kept at their total
	const updateActiveVehicles = (updater) => {
		setDispatches((_dispatches) =>
			_dispatches.map((_dispatch) => {
				if (_dispatch.id !== activeTabId) return _dispatch
				const vehicles = updater(_dispatch.vehicles)
				return { ..._dispatch, vehicles, quantity: { ..._dispatch.quantity, value: sumVehicleQuantities(vehicles) } }
			})
		)
	}

	// another vehicle can only be added once the last one has a quantity
	const lastVehicle = activeDispatch?.vehicles?.[activeDispatch.vehicles.length - 1]
	const canAddVehicle = !lastVehicle || Number(lastVehicle.quantity) > 0

	const addVehicleHandler = () => {
		if (!canAddVehicle) return
		updateActiveVehicles((vehicles) => [...vehicles, emptyVehicle(newVehicleId())])
	}

	const removeVehicleHandler = (vehicleId) => updateActiveVehicles((vehicles) => vehicles.filter((vehicle) => vehicle.id !== vehicleId))

	const updateVehicle = (vehicleId, key) => (value) =>
		updateActiveVehicles((vehicles) => vehicles.map((vehicle) => (vehicle.id === vehicleId ? { ...vehicle, [key]: value } : vehicle)))

	// a new plan brings its own draft, so the tabs start over with a single empty form
	const selectPlanHandler = (plan) => {
		setSelectedPlan(plan)
		setDraft(null)
		setLogisticsPartnerList([])
		nextDispatchId.current = 2
		setDispatches([emptyDispatchForm(1)])
		setActiveTabId(1)

		dispatch(showLoader(true))
		getDispatchPlanDraftApi(plan?.fulfillment_plan_id)
			.then((response) => {
				setDraft(response)
				fetchLogisticsPartners(response?.enquiry_id ?? plan?.enquiry_id, response?.logistics_handler)
			})
			.catch((err) => {
				dispatch(showToast(true, err?.response?.data?.message || strings('something_went_wrong'), { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}

	useEffect(() => {
		dispatch(showLoader(true))
		getApprovedFulfillmentPlansApi({ page: 0, size: 1000 }, { orderIdFilter: orderId })
			.then((response) => {
				const plans = convertPagableData(response)?.data?.filter((plan) => !orderId || plan?.order_id === orderId) ?? []
				setPlanList(plans.map((plan) => ({ ...plan, display_name: displayPlanName(plan) })))
			})
			.catch((err) => {
				dispatch(showToast(true, err?.response?.data?.message || strings('something_went_wrong'), { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}, [])

	// runs after the list's own loader has been cleared, so the draft fetch keeps its loader
	useEffect(() => {
		if (preselectDone.current || !preselectedPlanId || !planList.length) return
		preselectDone.current = true

		const plan = planList.find((_plan) => _plan?.fulfillment_plan_id === preselectedPlanId)
		if (plan) selectPlanHandler(plan)
	}, [planList, preselectedPlanId])

	// The partners on offer are the ones that quoted on the plan's enquiry (same statuses as the
	// order's Logistics Quote tab), one entry per partner organisation. The handler defaults to MBOOK
	// when there are partners to pick from, otherwise it is the one the draft carries.
	const fetchLogisticsPartners = (enquiryId, draftLogisticsHandler) => {
		const setLogisticsHandler = (value) =>
			setDispatches((_dispatches) =>
				_dispatches.map((_dispatch) => ({ ..._dispatch, logistics_handler: { ..._dispatch.logistics_handler, value } }))
			)

		// never undefined: a Select fed undefined data rebuilds its option list on every render
		setLogisticsPartnerList([])
		if (!enquiryId) {
			setLogisticsHandler(draftLogisticsHandler ?? null)
			return
		}

		getLogisticsByEnquiryId(enquiryId, 'ACTIVE, ORDER_PLACED')
			.then((response) => {
				const partners = new Map()
				;(response ?? []).forEach((quote) => {
					const partner = quote?.logistics_partner
					const partnerKey = partner?.organisation_id ?? partner?.user_id
					// the partner's first quote, whose freight goes into the dispatch plans created with this partner
					if (partnerKey && !partners.has(partnerKey))
						partners.set(partnerKey, {
							...partner,
							display_name: displayName(partner),
							lumpsum_logistics_quote: quote?.lumpsum_logistics_quote,
							logistics_quote_per_mt: quote?.logistics_quote_per_mt,
						})
				})
				const partnerList = [...partners.values()]
				setLogisticsPartnerList(partnerList)
				setLogisticsHandler(partnerList.length ? LOGISTICS_HANDLER_TYPES.MBOOK : draftLogisticsHandler ?? null)
				// preselect the first partner unless one has been picked already. Checked on the latest tabs,
				// as the ones this call was made with are gone once the plan resets them.
				if (partnerList.length)
					setDispatches((_dispatches) =>
						_dispatches[0]?.logistics_partner?.value
							? _dispatches
							: _dispatches.map((_dispatch) => ({
									..._dispatch,
									logistics_partner: { ..._dispatch.logistics_partner, value: partnerList[0] },
							  }))
					)
			})
			.catch(() => {
				setLogisticsHandler(draftLogisticsHandler ?? null)
				dispatch(showToast(true, strings('error_while_fetching_logistics_partner'), { type: TOAST_TYPE.ERROR }))
			})
	}

	// one row per vehicle, edited in place. Only the quantity is mandatory.
	const vehicleColumns = [
		{
			label: strings('vehicle', 'number'),
			key: (vehicle) => vehicle,
			renderer: (vehicle) => (
				<Input
					id={`vehicle-number-${vehicle.id}`}
					placeholder={strings(['enter', 'vehicle', 'number'])}
					value={vehicle.vehicle_number}
					onChange={(e) => updateVehicle(vehicle.id, 'vehicle_number')(e?.target?.value?.toUpperCase())}
				/>
			),
			sortable: false,
			mobile: true,
		},
		{
			label: strings('driver', 'number'),
			key: (vehicle) => vehicle,
			renderer: (vehicle) => {
				const invalidDriverNumber = !!vehicle.driver_number && !DRIVER_NUMBER_PATTERN.test(vehicle.driver_number)
				return (
					<Input
						id={`driver-number-${vehicle.id}`}
						placeholder={strings(['enter', 'driver', 'number'])}
						value={vehicle.driver_number}
						maxLength={10}
						inputMode='numeric'
						error={invalidDriverNumber ? strings('enter_valid_num') : undefined}
						onChange={(e) => updateVehicle(vehicle.id, 'driver_number')(e?.target?.value?.replace(/\D/g, ''))}
					/>
				)
			},
			sortable: false,
			mobile: true,
		},
		{
			label: quantityLabel(draft?.input_type),
			key: (vehicle) => vehicle,
			renderer: (vehicle) => (
				<Input
					id={`vehicle-quantity-${vehicle.id}`}
					type='number'
					min={0}
					placeholder={strings('quantity')}
					value={vehicle.quantity}
					required
					onChange={(e) => updateVehicle(vehicle.id, 'quantity')(e?.target?.value)}
				/>
			),
			sortable: false,
			mobile: true,
		},
		{
			label: '',
			key: (vehicle) => vehicle,
			renderer: (vehicle) => (
				<VehicleRemoveButton type='button' title={strings('remove')} onClick={() => removeVehicleHandler(vehicle.id)}>
					<DeleteOutline />
				</VehicleRemoveButton>
			),
			align: 'center',
			sortable: false,
			mobile: true,
		},
	]

	const renderVehicles = () => (
		<SectionBlock>
			<VehicleCardHeader>
				<SectionHeading>{strings(['vehicle', 'details'])}</SectionHeading>
				<Button
					type='button'
					variant='ghost'
					color='primary'
					size='small'
					iconPrefix={<Add />}
					disabled={!canAddVehicle}
					onClick={addVehicleHandler}
				>
					{strings(['add', 'vehicle'])}
				</Button>
			</VehicleCardHeader>
			<Table
				columns={vehicleColumns}
				data={activeDispatch.vehicles}
				rowsToDisplay={100}
				totalItemCount={activeDispatch.vehicles.length}
				onPageChange={() => {}}
				rowClickable={false}
				hideFooter
				emptyState={{ text: strings(['no', 'vehicle', 'added']) }}
			/>
		</SectionBlock>
	)

	// the outlet wrapper gives the page a full height, as Manage Order does for Create Shipment
	// under an order the Manage Order page already provides the wrapper
	const PageWrapper = orderId ? Fragment : OutletWrapper

	createHandlerRef.current = createDispatchPlansHandler

	return (
		<PageWrapper>
			<CreateShipmentPage>
				<AddDispatchPlanHeader>
					<Select
						id='select-fulfillment-plan'
						label='Buyer | Buyer PO | Supplier | Supplier PO | Fulfillment Id'
						placeholder='Select Order'
						data={planList}
						value={selectedPlan ?? {}}
						primaryKey='fulfillment_plan_id'
						displayKey='display_name'
						onChange={selectPlanHandler}
					/>
					<AddDispatchPlanLogistics>
						<Select
							id='dispatch-logistics-handler'
							label={commonDispatch.logistics_handler.label}
							placeholder={commonDispatch.logistics_handler.placeholder}
							data={logisticsHandlerTypes}
							value={logisticsHandlerTypes.find((type) => type.key === commonDispatch.logistics_handler.value) ?? {}}
							primaryKey='key'
							displayKey='label'
							disabled={!selectedPlan || commonDispatch.logistics_handler.disabled}
							onChange={(type) => updateAllDispatches('logistics_handler')(type?.key)}
						/>
						<Select
							id='dispatch-logistics-partner'
							label={commonDispatch.logistics_partner.label}
							placeholder={commonDispatch.logistics_partner.placeholder}
							data={logisticsPartnerList}
							value={commonDispatch.logistics_partner.value ?? {}}
							primaryKey='organisation_id'
							displayKey='display_name'
							disabled={!selectedPlan || commonDispatch.logistics_partner.disabled}
							onChange={updateAllDispatches('logistics_partner')}
						/>
						<Button
							type='button'
							variant='outline'
							color='primary'
							onClick={() => navigate(paths.admin_manage_enquiry_order_edit_logistics_quote(selectedPlan?.order_id ?? orderId))}
						>
							{strings(['add', 'logistics'])}
						</Button>
					</AddDispatchPlanLogistics>
				</AddDispatchPlanHeader>
				<DispatchPlanGrid>
					{/* nothing to show until a plan is picked */}
					{selectedPlan?.fulfillment_plan_id && (
						<LeftSection>
							{draft && (
								// two label-value pairs per row, at every breakpoint
								<GridListWrapper itemPerRow={[2, 2, 2]}>
									{getDraftDetails({ ...selectedPlan, ...draft }).map((detail) => (
										<DetailField key={detail.label} span={detail.span}>
											<DetailLabel>{detail.label}</DetailLabel>
											<DetailValue>{detail.value}</DetailValue>
										</DetailField>
									))}
								</GridListWrapper>
							)}
						</LeftSection>
					)}
					<RightCards>
						{/* the dispatch forms belong to a plan, so nothing is shown until one is picked */}
						{selectedPlan?.fulfillment_plan_id && (
							<>
								<DispatchSectionBlock>
									<ShipmentTabs
										tabs={tabs}
										activeId={activeTabId}
										onTabChange={setActiveTabId}
										onAddTab={addDispatchHandler}
										onRemoveTab={removeDispatchHandler}
										addDisabled={!canAddDispatch}
										hideAutofill
										rightAction={{ label: strings(['previous', 'dispatches']), onClick: previousDispatchesHandler }}
										addTitle={strings(['add', 'dispatch'])}
									/>
									{activeDispatch && (
										<DispatchFieldsRow>
											<Calendar
												id='dispatch-plan-start-date'
												position={isStacked ? 'top' : 'bottom'}
												label={activeDispatch.plan_start_date.label}
												placeholder={activeDispatch.plan_start_date.placeholder}
												value={activeDispatch.plan_start_date.value}
												disabled={activeDispatch.plan_start_date.disabled}
												onChange={updateActiveDispatch('plan_start_date')}
											/>
											<Input
												id='dispatch-quantity'
												type='number'
												label={quantityLabel(draft?.input_type)}
												placeholder={activeDispatch.quantity.placeholder}
												value={activeDispatch.quantity.value}
												disabled={activeDispatch.quantity.disabled}
												required={activeDispatch.quantity.required}
											/>
										</DispatchFieldsRow>
									)}
								</DispatchSectionBlock>
								{activeDispatch && renderVehicles()}
							</>
						)}
					</RightCards>
				</DispatchPlanGrid>

				{/* only when this page stands on its own — inside Manage Order the tab shell renders the bar */}
				{typeof setFooterActions !== 'function' && (
					<StickyFooter>
						<Button type='button' color='primary' disabled={!canCreate} onClick={createDispatchPlansHandler}>
							{strings('create')}
						</Button>
					</StickyFooter>
				)}
			</CreateShipmentPage>
		</PageWrapper>
	)
}

export default AddDispatchPlanV2
